import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { ImgMovement, ImgMovementDisabled } from '../../interfaces';
import { UiHelpersService } from '../../services';

@Component({
  selector: 'phoenix-img-viewer',
  templateUrl: './img-viewer.component.html',
  standalone: false,
})
export class ImgViewerComponent {
  uiHelpers = inject(UiHelpersService);
  dialog = inject(MatDialog);

  @Input() img!: string;
  @Input() imgDownload!: string;
  @Input() altText!: string;
  @Input() zoomImg = true;
  @Input() deleteButton = false;
  @Input() staticImg = false;
  @Input() uploadButton = false;
  @Input() movementArrows = false;
  @Input() imgActions = true;
  @Input() centerDivs = false;
  @Input() movementDisabled: ImgMovementDisabled = null;
  @Input() isInDialog = false;

  @Output() selectedFile = new EventEmitter<File>();
  @Output() deletedImage = new EventEmitter();
  @Output() clickImage = new EventEmitter();
  @Output() imgMovement = new EventEmitter<ImgMovement>();

  pinImg = false;
  lensWidth = 100;
  lensHeight = 100;
  boxExtraSize = 0;

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Only respond to keyboard events if movement arrows are enabled
    if (
      !this.movementArrows ||
      this.uiHelpers.isInputFocused(event.target as HTMLElement) ||
      !this.shouldHandleKeyboardEvents()
    ) {
      return;
    }

    //  Left Arrow
    if (event.key === 'ArrowLeft') {
      // Only emit if left movement is not disabled
      if (
        this.movementDisabled !== 'previous' &&
        this.movementDisabled !== 'both'
      ) {
        this.imgMovement.emit('previous');
        event.preventDefault(); // Prevent default browser behavior
      }
    }

    //  Right Arrow
    if (event.key === 'ArrowRight') {
      // Only emit if right movement is not disabled
      if (
        this.movementDisabled !== 'next' &&
        this.movementDisabled !== 'both'
      ) {
        this.imgMovement.emit('next');
        event.preventDefault(); // Prevent default browser behavior
      }
    }
  }

  // Helper method to determine if this instance should handle keyboard events
  private shouldHandleKeyboardEvents(): boolean {
    // Case 1: If we're inside a dialog, only handle events if we're the only dialog open
    if (this.isInDialog) {
      // Get the number of open dialogs
      const openDialogs = this.dialog.openDialogs;
      // If we're in a dialog and there's only one dialog open, we should handle events
      return openDialogs.length === 1;
    }

    // Case 2: If we're not in a dialog, only handle events if no dialogs are open
    return this.dialog.openDialogs.length === 0;
  }

  uploadImgs(imgs: File[]) {
    if (imgs.length > 0) {
      this.selectedFile.emit(imgs[imgs.length - 1]);
    }
  }

  onImageLoadError(e: ErrorEvent) {
    (e.target as HTMLImageElement).src = this.uiHelpers.placeholderPath;
    this.uiHelpers.errorSnackbar(`Some images failed to load.`, 5000);
  }

  delete() {
    this.pinImg = false;
    this.deletedImage.emit(this.img);
  }
}
